import type { IFuseOptions } from "fuse.js";

import {
  getMentionSectionLabel,
  type MentionSection,
  RELATED_MENTION_SECTION_KEY,
} from "src/unifiedCopilot/chat/promptBox/mentions/mentionSections";
import type { ReferenceContextualEntityType } from "src/unifiedCopilot/context/entityDefinitions";
import {
  useAmbientContextualEntities,
  useAutomaticallyAddedContextualEntities,
  useContextualEntities,
} from "src/unifiedCopilot/context/internalHooks";
import {
  type ContextualEntity,
  type ContextualEntityKey,
  contextualEntityKey,
  type ReferenceContextualEntity,
} from "src/unifiedCopilot/context/types";
import { useFuseSearch } from "src/utils/useFuseSearch";

const ENTITY_TYPE_ORDER: ReferenceContextualEntityType[] = [
  "flow",
  "flow-version",
  "node",
  "dataset",
  "agent",
  "agent-version",
  "agent-tool",
  "flow-parameter",
  "decision",
  "testrun",
  "case-template",
  "case-template-version",
  "case-queue",
];

const FUSE_OPTIONS: IFuseOptions<ReferenceContextualEntity> = {
  keys: ["displayName"],
  ignoreLocation: true,
  threshold: 0.35,
};

const isReferenceContextualEntity = (
  entity: ContextualEntity,
): entity is ReferenceContextualEntity => entity.kind === "reference";

const referenceKeys = (
  entities: ContextualEntity[],
): Set<ContextualEntityKey> =>
  new Set(
    entities.filter(isReferenceContextualEntity).map(contextualEntityKey),
  );

const groupByEntityType = (
  entities: ReferenceContextualEntity[],
): MentionSection[] =>
  ENTITY_TYPE_ORDER.map((type) => ({
    key: type,
    label: getMentionSectionLabel(type),
    entities: entities.filter((entity) => entity.type === type),
  }));

export type ContextualEntityMentionSearch = {
  hasEntities: boolean;
  search: (query: string) => MentionSection[];
};

/**
 * The `@` mention catalogue as ordered, non-empty sections: first the current
 * page's ambient entities that are not already attached as context chips
 * ("Related to current page"), then the full catalogue grouped by entity type.
 * Related entities intentionally appear in both; chip entities only in their
 * type section, since they are already in the prompt.
 */
export const useContextualEntityMentionSearch =
  (): ContextualEntityMentionSearch => {
    const entities = useContextualEntities();
    const ambientEntities = useAmbientContextualEntities();
    const automaticEntities = useAutomaticallyAddedContextualEntities();
    const fuzzySearch = useFuseSearch(entities, FUSE_OPTIONS);

    const automaticKeys = referenceKeys(automaticEntities);
    const relatedKeys = new Set(
      [...referenceKeys(ambientEntities)].filter(
        (key) => !automaticKeys.has(key),
      ),
    );

    const search = (query: string): MentionSection[] => {
      const typedSections = groupByEntityType(fuzzySearch(query.trim()));
      const relatedSection: MentionSection = {
        key: RELATED_MENTION_SECTION_KEY,
        label: getMentionSectionLabel(RELATED_MENTION_SECTION_KEY),
        entities: typedSections.flatMap((section) =>
          section.entities.filter((entity) =>
            relatedKeys.has(contextualEntityKey(entity)),
          ),
        ),
      };

      return [relatedSection, ...typedSections].filter(
        (section) => section.entities.length > 0,
      );
    };

    return {
      hasEntities: entities.length > 0,
      search,
    };
  };
